import { Blackout, BlackoutDate, SpaceService } from "../../../services/space.service";
import { ProfileUtil } from "../../s25-datepattern/profile.util";
import { S25Util } from "../../../util/s25-util";
import { EventProfile } from "../../../pojo/EventI";
import { Event } from "../../../pojo/Event";
import { TimeModelI } from "../../../pojo/TimeModelI";
import { S25Assert } from "../../../util/s25.assert";
import Occurrence = Event.Occurrence;

// Pattern edits never use the server-expanded date[] (dates come from profile_code), so a small
// window keeps that fetch fast. Ad hoc edits use the blackout's own start/end from list scope.
const EDIT_DETAIL_WINDOW_DAYS = 30;

export class BlackoutUtil {
    /** Pattern blackouts carry a full recurrence in profile_code. */
    public static canExpandDatesFromProfileCode(blackout?: Blackout): boolean {
        return ProfileUtil.isFiniteRecurrenceProfileCode(blackout?.profile_code);
    }

    public static getDatesFromProfileCode(blackout: Blackout): BlackoutDate[] {
        return BlackoutUtil.occurrencesToDates(BlackoutUtil.getOccurrences(blackout));
    }

    public static datesToOccurrences(dates?: BlackoutDate[]): Occurrence[] {
        return (dates ?? []).map((blackoutDate) => ({
            evStartDt: new Date(blackoutDate.start_dt),
            evEndDt: new Date(blackoutDate.end_dt),
        }));
    }

    public static occurrencesToDates(occurrences: Occurrence[]): BlackoutDate[] {
        return occurrences.map((occurrence) => ({
            start_dt: S25Util.date.toS25ISODateTimeStr(occurrence.evStartDt)!,
            end_dt: S25Util.date.toS25ISODateTimeStr(occurrence.evEndDt)!,
        }));
    }

    /**
     * Pattern blackouts derive their end from profile_code. Ad hoc blackouts use the latest date[]
     * row; list scope already returns that blackout's true max end date.
     */
    public static getEndDate(blackout?: Blackout): Date | undefined {
        return BlackoutUtil.getPatternEndDate(blackout) || BlackoutUtil.getLatestListedEndDate(blackout);
    }

    /**
     * Load a blackout for edit/copy. Pattern dates are expanded in the edit modal, not here.
     *
     * `listed` is the list-scope row: pattern edits use a short window anchored at its start so the
     * server does not expand the series; ad hoc edits use that row's true start/end so one fetch
     * returns the full date[].
     */
    public static async prepareForEdit(profileName: string, listed?: Blackout): Promise<Blackout | null> {
        const baseQuery = `?itemName=${encodeURIComponent(profileName)}&all_blackouts=1`;
        const windowQuery = BlackoutUtil.editWindowQuery(listed);
        const data = await SpaceService._getBlackouts(windowQuery ? `${baseQuery}&${windowQuery}` : baseQuery);
        const blackout = data.blackout?.[0];
        if (!blackout) return null;

        blackout.init_start_dt = S25Util.date.dropTZString(blackout.init_start_dt);
        blackout.init_end_dt = S25Util.date.dropTZString(blackout.init_end_dt);

        if (
            BlackoutUtil.canExpandDatesFromProfileCode(blackout) ||
            blackout.rec_type === EventProfile.RecType.DateOrTimeList
        ) {
            return blackout;
        }

        blackout.date = [{ start_dt: blackout.init_start_dt, end_dt: blackout.init_end_dt }];
        return blackout;
    }

    private static editWindowQuery(listed?: Blackout): string | undefined {
        const startDt = listed?.init_start_dt;
        if (!BlackoutUtil.canExpandDatesFromProfileCode(listed)) {
            return BlackoutUtil.dateWindowQuery(startDt, BlackoutUtil.getEndDate(listed));
        }

        const start = S25Util.date.parse(startDt);
        S25Assert(start, "Pattern blackout is missing a start date; cannot fetch a bounded edit window");
        return BlackoutUtil.dateWindowQuery(
            start,
            S25Util.date.addDays(S25Util.date.clone(start), EDIT_DETAIL_WINDOW_DAYS),
        );
    }

    private static dateWindowQuery(startDt?: string | Date, endDt?: string | Date): string | undefined {
        const start = S25Util.date.parse(startDt);
        if (!start) return undefined;
        const end = S25Util.date.parse(endDt);
        const windowEnd = end && end >= start ? end : start;
        return `start_dt=${S25Util.date.toS25ISODateStrStartOfDay(start)}&end_dt=${S25Util.date.toS25ISODateStrEndOfDay(windowEnd)}`;
    }

    private static getOccurrences(blackout: Blackout): Occurrence[] {
        const timeModel = BlackoutUtil.toTimeModel(blackout);
        if (!timeModel || !blackout.profile_code) return [];
        return ProfileUtil.getOccurrencesFromProfileCode(timeModel, blackout.profile_code);
    }

    private static toTimeModel(blackout: Blackout): TimeModelI | undefined {
        const evStartDt = S25Util.date.parse(blackout.init_start_dt);
        const evEndDt = S25Util.date.parse(blackout.init_end_dt);
        if (!evStartDt || !evEndDt) return undefined;
        return ProfileUtil.emptyTimeModel(evStartDt, evEndDt);
    }

    private static getPatternEndDate(blackout?: Blackout): Date | undefined {
        const profileCode = blackout?.profile_code;
        if (!profileCode || ProfileUtil.getProfileCodeEndingType(profileCode) === "none") return undefined;

        const evEndDt = S25Util.date.parse(blackout.init_end_dt);
        if (!evEndDt) return undefined;

        const throughDate = ProfileUtil.getProfileCodeThroughDate(profileCode);
        if (throughDate) return S25Util.date.copyTime(throughDate, evEndDt);

        const occurrences = BlackoutUtil.getOccurrences(blackout);
        return occurrences.reduce(
            (latest, occurrence) => (occurrence.evEndDt > latest ? occurrence.evEndDt : latest),
            evEndDt,
        );
    }

    private static getLatestListedEndDate(blackout?: Blackout): Date | undefined {
        const endDates = (blackout?.date ?? [])
            .map((blackoutDate) => S25Util.date.parse(blackoutDate.end_dt))
            .filter(Boolean);
        if (!endDates.length) return S25Util.date.parse(blackout?.init_end_dt) ?? undefined;
        return endDates.reduce((latest, endDate) => (endDate > latest ? endDate : latest));
    }
}
